<template>
  <div class="chat-announcements-feed chat-system-announcements-feed">
    <div v-if="loading" class="chat-announcements-feed__state text-400-default neutral_2">Đang tải...</div>
    <div v-else-if="!items.length" class="chat-announcements-feed__state text-400-default neutral_2">{{ emptyText }}</div>
    <ul v-else class="chat-announcements-feed__list">
      <li
        v-for="item in items"
        :key="'sys-' + item.message_id + '-' + item.group_id"
        class="chat-announcements-feed__item"
        :class="{ 'chat-announcements-feed__item--active': isActiveHighlight(item) }"
        role="button"
        tabindex="0"
        @click="onSelect(item)"
        @keydown.enter.prevent="onSelect(item)"
      >
        <div class="chat-announcements-feed__title text-400-medium neutral_1">{{ item.title || 'Thông báo' }}</div>

        <ul v-if="payloadEntries(item).length" class="chat-announcements-feed__payload-list">
          <li
            v-for="row in payloadEntries(item)"
            :key="item.message_id + '-payload-' + row.key"
            class="chat-announcements-feed__payload-item text-300-default neutral_2"
          >
            <span class="chat-announcements-feed__payload-key">{{ row.key }}:</span>
            <span class="chat-announcements-feed__payload-value-wrap">
              <a
                v-if="isPayloadUrl(row.value)"
                :href="payloadUrlHref(row.value)"
                class="chat-announcements-feed__payload-value chat-announcements-feed__payload-value--link"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >{{ formatPayloadValue(row.value) }}</a>
              <strong v-else class="chat-announcements-feed__payload-value neutral_1">{{ formatPayloadValue(row.value) }}</strong>
            </span>
          </li>
        </ul>

        <div v-if="showGroupName && botGroupName(item)" class="chat-announcements-feed__bot-row">
          <image-template
            class="chat-announcements-feed__avatar"
            :width="'24px'"
            :height="'24px'"
            :size="'small'"
            :image="botAvatar(item)"
            :thumbnail="botThumbnail(item)"
            :name="botGroupName(item)"
            img-type="group"
          />
          <span class="chat-announcements-feed__bot-name text-300-default neutral_2">{{ botGroupName(item) }}</span>
        </div>

        <div v-if="item.expires_at" class="chat-announcements-feed__footer">
          <span
            class="chat-announcements-feed__countdown-badge"
            :class="{ 'chat-announcements-feed__countdown-badge--expired': isExpired(item) }"
          >{{ expiryBadgeText(item) }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import axios from 'axios';
import { navigateToChatAnnouncement } from '../../../../../helper/chatAnnouncementNav';
import {
  announcementExpiryBadgeText,
  announcementIsActiveHighlight,
  announcementIsExpiredLive,
  announcementPayloadDataEntries,
  announcementPayloadUrlHref,
  formatAnnouncementPayloadValue,
  isAnnouncementPayloadUrl,
} from '../../../../../helper/chatAnnouncementDisplay';
import ImageTemplate from '../../../../../components/ImageTemplate';

export default {
  name: 'ChatSystemAnnouncementsFeed',
  components: { ImageTemplate },
  props: {
    limit: {
      type: Number,
      default: 10,
    },
    showGroupName: {
      type: Boolean,
      default: true,
    },
    emptyText: {
      type: String,
      default: 'Không có thông báo mới',
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      countdownTick: 0,
      countdownTimer: null,
    };
  },
  watch: {
    limit() {
      this.load();
    },
  },
  mounted() {
    this.load();
    this.countdownTimer = setInterval(() => {
      this.countdownTick += 1;
    }, 1000);
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  },
  methods: {
    async load() {
      this.loading = true;
      this.items = [];
      try {
        const res = await axios.get('chat/groups/recent-system-announcements', {
          params: { limit: this.limit },
        });
        const body = res && res.data;
        if (body && body.status && Array.isArray(body.data)) {
          this.items = body.data;
        }
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    botGroupName(item) {
      const g = item && item.chat_group;
      if (g && g.name) return g.name;
      return item && item.chat_group_name ? item.chat_group_name : '';
    },
    botAvatar(item) {
      const g = item && item.chat_group;
      return g && g.avatar ? g.avatar : null;
    },
    botThumbnail(item) {
      const g = item && item.chat_group;
      return g && g.thumbnail ? g.thumbnail : null;
    },
    payloadEntries(item) {
      return announcementPayloadDataEntries(item && item.payload);
    },
    formatPayloadValue(val) {
      return formatAnnouncementPayloadValue(val);
    },
    isPayloadUrl(val) {
      return isAnnouncementPayloadUrl(val);
    },
    payloadUrlHref(val) {
      return announcementPayloadUrlHref(val);
    },
    isExpired(item) {
      return announcementIsExpiredLive(item && item.expires_at, this.countdownTick);
    },
    expiryBadgeText(item) {
      return announcementExpiryBadgeText(item && item.expires_at, this.countdownTick);
    },
    isActiveHighlight(item) {
      return announcementIsActiveHighlight(item, this.countdownTick);
    },
    onSelect(item) {
      this.$emit('select', item);
      navigateToChatAnnouncement(this, {
        groupId: item.group_id,
        messageId: item.message_id,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.chat-announcements-feed__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.chat-announcements-feed__item {
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover .chat-announcements-feed__title {
    color: #198eeb;
  }
}

.chat-announcements-feed__item--active {
  margin: 6px 0;
  padding: 10px 10px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(25, 142, 235, 0.1) 0%, rgba(99, 102, 241, 0.07) 100%);
  border: 1px solid rgba(25, 142, 235, 0.28);
  box-shadow: 0 2px 8px rgba(25, 142, 235, 0.08);

  &:last-child {
    border-bottom: 1px solid rgba(25, 142, 235, 0.28);
  }
}

.chat-announcements-feed__title {
  line-height: 1.35;
}

.chat-announcements-feed__footer {
  margin-top: 8px;
}

.chat-announcements-feed__countdown-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
  color: #0b5cab;
  background: rgba(25, 142, 235, 0.14);
  border: 1px solid rgba(25, 142, 235, 0.28);
}

.chat-announcements-feed__countdown-badge--expired {
  color: #8a4b00;
  background: rgba(245, 158, 11, 0.14);
  border-color: rgba(245, 158, 11, 0.35);
}

.chat-announcements-feed__payload-list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}

.chat-announcements-feed__payload-item {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 4px;
  line-height: 1.4;

  &:first-child {
    margin-top: 0;
  }
}

.chat-announcements-feed__payload-key {
  flex-shrink: 0;
}

.chat-announcements-feed__payload-value-wrap {
  flex: 1;
  min-width: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
}

.chat-announcements-feed__payload-value {
  font-weight: 600;
  line-height: 1.4;
}

.chat-announcements-feed__payload-value--link {
  color: #198eeb;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.chat-announcements-feed__bot-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  min-height: 24px;
}

.chat-announcements-feed__avatar {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid #eee;
  aspect-ratio: 1 / 1;

  .image-template__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.chat-announcements-feed__bot-name {
  min-width: 0;
  line-height: 1.3;
}

.chat-announcements-feed__state {
  padding: 8px 0;
}
</style>
